import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import {
  ALWAYS_ROW_INFORMATION_CODES,
  cableFiguresSourceSchema,
  cableSizingStatusSchema,
  circuitKindSchema,
  conductorMaterialSchema,
  deviceKindSchema,
  disconnectionTimeOriginSchema,
  earthFaultLoopStatusSchema,
  earthFaultLoopWarningCodeSchema,
  externalImpedanceOriginSchema,
  faultTypeSchema,
  insulationMaterialSchema,
  limitBasisSchema,
  maximumLoopImpedanceOriginSchema,
  mcbTypeSchema,
  operatingCurrentBasisSchema,
  operatingCurrentOriginSchema,
  resistanceBasisSchema,
  shortCircuitCaseSchema,
  supplyKindSchema,
  systemKindSchema,
  temperatureBasisSchema,
  voltageFactorOriginSchema,
} from "./earthFaultLoopContract";
import { exactDecimalSchema } from "./faultContract";
import {
  CHOOSE_A_FAULT_RUN,
  type FaultRunChoice,
  type FaultRunFaultLevel,
  getFaultRun,
  listFaultRunsForRevision,
} from "./faultLink";
import { ApiError, describeApiFieldError } from "./http";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const optionalDecimal = exactDecimalSchema.nullable().optional();
const optionalSource = (maxLength = 300) => z.string().trim().min(1).max(maxLength).optional();

const RUNS_URL = "/api/v1/electrical/earth-fault-loop/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The selector's own sentence for a row that names no cable run of the revision. */
export const CHOOSE_A_CABLE_RUN = "Choose a cable run of this project revision.";

// The fault-run link (selector list, fault level, the selector's sentence) is shared with
// the other protection studies and lives in faultLink; re-exported so the form keeps one import.
export { CHOOSE_A_FAULT_RUN, getFaultRun, listFaultRunsForRevision };
export type { FaultRunChoice, FaultRunFaultLevel };

// The choices a circuit row needs; the backend imposes no default on them.
export const REQUIRED_ROW_CHOICES = ["circuit_kind"] as const;
export const REQUIRED_DEVICE_CHOICES = ["device_kind"] as const;

// Ze declared by the engineer with its source: allowed, not linked evidence (D3).
export const declaredExternalImpedanceSchema = z
  .object({
    value_ohm: exactDecimalSchema,
    source: optionalSource(),
  })
  .strict();

export const minimumVoltageFactorSchema = z
  .object({
    value: exactDecimalSchema,
    source: optionalSource(),
  })
  .strict();

// The conductor figures of a row typed by the engineer with their source (D5). A blank
// protective conductor area is left out: it means NOT ESTABLISHED, never assumed.
export const cableFiguresSchema = z
  .object({
    line_conductor_area_mm2: exactDecimalSchema,
    protective_conductor_area_mm2: exactDecimalSchema.optional(),
    conductor_material: conductorMaterialSchema.optional(),
    route_length_m: exactDecimalSchema,
    parallel_runs: z.number().int().min(1).max(12).optional(),
    protective_conductor_runs: z.number().int().min(1).max(12).optional(),
    source: optionalSource(),
  })
  .strict()
  .superRefine((cable, ctx) => {
    if (cable.conductor_material === undefined) {
      ctx.addIssue({
        code: "invalid_type",
        expected: "string",
        received: "undefined",
        message: "conductor_material is required",
        path: ["conductor_material"],
      });
    }
  });

// The device as declared (section 5): identity, the required time with its source, Ia or
// the maximum Zs with its source. Every blank item is left out: NOT ESTABLISHED.
export const protectiveDeviceRequestSchema = z
  .object({
    device_code: z.string().trim().min(1).max(50),
    device_kind: deviceKindSchema.optional(),
    mcb_type: mcbTypeSchema.optional(),
    rated_current_a: exactDecimalSchema,
    rated_voltage_v: exactDecimalSchema.optional(),
    characteristic_text: z.string().trim().min(1).max(200).optional(),
    required_disconnection_time_s: exactDecimalSchema.optional(),
    time_source: optionalSource(),
    operating_current_a: exactDecimalSchema.optional(),
    operating_current_source: optionalSource(),
    maximum_loop_impedance_ohm: exactDecimalSchema.optional(),
    maximum_loop_impedance_source: optionalSource(),
    operating_current_basis: operatingCurrentBasisSchema.optional(),
  })
  .strict()
  .superRefine((device, ctx) => {
    for (const field of REQUIRED_DEVICE_CHOICES) {
      if (device[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
  });

// One circuit leaving the board (D2): its cable from exactly one place - a stored EOS-06
// cable run of the revision, or the typed figures - as the backend requires.
export const circuitRowRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    circuit_kind: circuitKindSchema.optional(),
    cable_run_id: z.string().optional(),
    cable: cableFiguresSchema.optional(),
    line_resistance_ohm_per_km_20c: exactDecimalSchema.optional(),
    protective_resistance_ohm_per_km_20c: exactDecimalSchema.optional(),
    resistance_source: optionalSource(),
    reactance_ohm_per_km: exactDecimalSchema.optional(),
    reactance_source: optionalSource(),
    conductor_temperature_c: exactDecimalSchema.optional(),
    temperature_source: optionalSource(),
    device: protectiveDeviceRequestSchema,
    minimum_margin_percent: exactDecimalSchema.optional(),
    sld_element_code: z.string().trim().min(1).max(50).optional(),
  })
  .strict()
  .superRefine((row, ctx) => {
    for (const field of REQUIRED_ROW_CHOICES) {
      if (row[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
    if ((row.cable_run_id === undefined) === (row.cable === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "choose one cable source: a linked cable run, or the typed cable figures",
        path: ["cable_run_id"],
      });
    } else if (row.cable_run_id !== undefined && !UUID.test(row.cable_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_CABLE_RUN, path: ["cable_run_id"] });
    }
  });

export const earthFaultLoopRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    bus_code: z.string().trim().min(1).max(50),
    system_kind: systemKindSchema.optional(),
    supply_kind: supplyKindSchema.optional(),
    system_nominal_voltage_v: exactDecimalSchema,
    frequency_hz: exactDecimalSchema,
    // At most one of the two: a stored EOS-04 earth-fault run of the revision, or a declared
    // value; neither is NOT ESTABLISHED (nothing compared). A malformed run id is named with
    // the selector's own sentence.
    fault_run_id: z.string().optional(),
    external_impedance: declaredExternalImpedanceSchema.optional(),
    minimum_voltage_factor: minimumVoltageFactorSchema.optional(),
    nominal_line_to_earth_voltage_v: exactDecimalSchema.optional(),
    nominal_voltage_source: optionalSource(),
    circuits: z.array(circuitRowRequestSchema).min(1).max(200),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    sld_element_code: z.string().trim().min(1).max(50).optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    for (const field of ["system_kind", "supply_kind"] as const) {
      if (values[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
    if (values.fault_run_id !== undefined && values.external_impedance !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "Choose one external impedance source: a linked fault run, or a declared value.",
        path: ["fault_run_id"],
      });
    } else if (values.fault_run_id !== undefined && !UUID.test(values.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }
    const codes = values.circuits.map((row) => row.code);
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({
        code: "custom",
        message: "circuit codes must be unique within a study",
        path: ["circuits"],
      });
    }
  });

export const earthFaultLoopWarningSchema = z
  .object({
    code: earthFaultLoopWarningCodeSchema,
    message: z.string(),
  })
  .strict();

export const protectiveDeviceResponseSchema = z
  .object({
    device_code: z.string(),
    device_kind: deviceKindSchema,
    mcb_type: mcbTypeSchema.nullable(),
    rated_current_a: z.string(),
    rated_voltage_v: z.string().nullable(),
    characteristic_text: z.string().nullable(),
    required_disconnection_time_s: z.string().nullable(),
    time_source: z.string().nullable(),
    operating_current_a: z.string().nullable(),
    operating_current_source: z.string().nullable(),
    maximum_loop_impedance_ohm: z.string().nullable(),
    maximum_loop_impedance_source: z.string().nullable(),
    operating_current_basis: operatingCurrentBasisSchema.nullable(),
  })
  .strict();

export const linkedCableRunResponseSchema = z
  .object({
    cable_run_id: z.string().uuid(),
    study_code: z.string(),
    revision_number: z.number().int(),
    run_status: cableSizingStatusSchema,
    insulation_material: insulationMaterialSchema.nullable(),
    insulation_maximum_temperature_c: z.string().nullable(),
  })
  .strict();

// A controlled table value the IN profile's applier wrote into the row (section 5.1).
export const registerTableValueSchema = z
  .object({
    value: z.string(),
    reference_id: z.string(),
    locator: z.string(),
    basis_text: z.string(),
  })
  .strict();

// One circuit as checked: every input echoed, every figure on its stated basis; a figure
// the engine could not form is null (F1: a field added later is optional on this side).
export const circuitRowResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    circuit_kind: circuitKindSchema,
    cable_source: cableFiguresSourceSchema,
    linked_cable_run: linkedCableRunResponseSchema.nullable(),
    line_conductor_area_mm2: z.string(),
    protective_conductor_area_mm2: z.string().nullable(),
    conductor_material: conductorMaterialSchema,
    route_length_m: z.string(),
    parallel_runs: z.number().int(),
    protective_conductor_runs: z.number().int(),
    resistivity_ohm_mm2_per_m: z.string(),
    temperature_coefficient_per_k: z.string(),
    line_resistance_basis: resistanceBasisSchema,
    protective_resistance_basis: resistanceBasisSchema.nullable(),
    line_resistance_20c_ohm: z.string(),
    protective_resistance_20c_ohm: z.string().nullable(),
    conductor_temperature_c: z.string().nullable(),
    temperature_basis: temperatureBasisSchema.nullable(),
    temperature_factor: z.string().nullable(),
    line_resistance_ohm: z.string().nullable(),
    protective_resistance_ohm: z.string().nullable(),
    reactance_ohm_per_km: z.string().nullable(),
    loop_reactance_ohm: z.string().nullable(),
    cable_impedance_20c_ohm: z.string().nullable(),
    cable_impedance_ohm: z.string().nullable(),
    loop_impedance_20c_ohm: z.string().nullable(),
    loop_impedance_ohm: z.string().nullable(),
    fault_current_a: z.string().nullable(),
    device: protectiveDeviceResponseSchema,
    required_disconnection_time_s: z.string().nullable(),
    disconnection_time_origin: disconnectionTimeOriginSchema,
    operating_current_a: z.string().nullable(),
    operating_current_origin: operatingCurrentOriginSchema,
    maximum_loop_impedance_ohm: z.string().nullable(),
    maximum_loop_impedance_origin: maximumLoopImpedanceOriginSchema,
    loop_impedance_margin_ohm: z.string().nullable(),
    margin_percent: z.string().nullable(),
    current_ratio: z.string().nullable(),
    disconnection_assured: z.boolean().nullable(),
    limit_basis: limitBasisSchema,
    minimum_margin_percent: z.string().nullable(),
    sld_element_code: z.string().nullable(),
    register_maximum_loop_impedance: registerTableValueSchema.nullable().optional(),
    register_disconnection_time: registerTableValueSchema.nullable().optional(),
    status: earthFaultLoopStatusSchema,
    warnings: z.array(earthFaultLoopWarningSchema),
  })
  .strict();

export const earthFaultLoopResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    bus_code: z.string(),
    system_kind: systemKindSchema,
    supply_kind: supplyKindSchema,
    system_nominal_voltage_v: z.string(),
    frequency_hz: z.string(),
    line_to_earth_voltage_v: z.string(),
    external_impedance_origin: externalImpedanceOriginSchema,
    external_loop_impedance_ohm: z.string().nullable(),
    external_impedance_basis: z.string().nullable(),
    external_impedance_source_text: z.string().nullable(),
    // The linked run, its study code, revision, case, fault type and bus exactly as the
    // stored result holds them; null for a declared or an absent Ze.
    fault_run_id: z.string().uuid().nullable(),
    fault_run_calculation_key: z.string().nullable(),
    fault_run_revision_number: z.number().int().nullable(),
    calculation_case: shortCircuitCaseSchema.nullable(),
    fault_type: faultTypeSchema.nullable(),
    fault_bus_code: z.string().nullable(),
    voltage_factor_origin: voltageFactorOriginSchema,
    minimum_voltage_factor: z.string().nullable(),
    voltage_factor_source: z.string().nullable(),
    comparison_voltage_v: z.string(),
    nominal_line_to_earth_voltage_v: optionalDecimal,
    nominal_voltage_source: z.string().nullable().optional(),
    jurisdiction_profile: jurisdictionProfileSchema,
    limit_basis: limitBasisSchema,
    sld_element_code: z.string().nullable(),
    rows: z.array(circuitRowResponseSchema),
    status: earthFaultLoopStatusSchema,
    warnings: z.array(earthFaultLoopWarningSchema),
    notes: z.string().nullable(),
  })
  .strict();

export const earthFaultLoopRunCreateRequestSchema = z
  .object({
    study: earthFaultLoopRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const earthFaultLoopRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: earthFaultLoopResponseSchema,
  })
  .strict();

export type CableFiguresRequest = z.infer<typeof cableFiguresSchema>;
export type ProtectiveDeviceRequest = z.infer<typeof protectiveDeviceRequestSchema>;
export type CircuitRowRequest = z.infer<typeof circuitRowRequestSchema>;
export type EarthFaultLoopRequest = z.infer<typeof earthFaultLoopRequestSchema>;
export type EarthFaultLoopWarning = z.infer<typeof earthFaultLoopWarningSchema>;
export type ProtectiveDeviceResponse = z.infer<typeof protectiveDeviceResponseSchema>;
export type LinkedCableRunResponse = z.infer<typeof linkedCableRunResponseSchema>;
export type RegisterTableValue = z.infer<typeof registerTableValueSchema>;
export type CircuitRowResponse = z.infer<typeof circuitRowResponseSchema>;
export type EarthFaultLoopResponse = z.infer<typeof earthFaultLoopResponseSchema>;
export type EarthFaultLoopRunCreateRequest = z.infer<typeof earthFaultLoopRunCreateRequestSchema>;
export type EarthFaultLoopRunResponse = z.infer<typeof earthFaultLoopRunResponseSchema>;

export { ALWAYS_ROW_INFORMATION_CODES };

function formatApiError(data: unknown, status: number, subject: string): string {
  return describeApiFieldError(data, status, `${subject} failed`);
}

function requestSignal(signal?: AbortSignal): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(30_000);
  return signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
}

/**
 * Calculate an earth-fault loop study and persist it as a run revision.
 *
 * With a project revision id the run is stored under that revision of the selected
 * project - and a linked fault run or cable run must then belong to the same revision;
 * without one it belongs to no project. An unchanged study returns the stored revision
 * with HTTP 200 instead of a new one (A11); both are valid here.
 */
export async function createEarthFaultLoopRun(
  payload: EarthFaultLoopRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<EarthFaultLoopRunResponse> {
  const validatedPayload = earthFaultLoopRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Earth-fault loop"), response.status);
  }

  const parsed = earthFaultLoopRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS earth-fault loop runs API.");
  }

  return parsed.data;
}

const CABLE_RUNS_URL = "/api/v1/electrical/cable/runs";

// What the cable-run selector shows from the runs list: run summaries only, never a result
// field (CalculationRunListResponse); the backend reads the chosen run's frozen result again
// when the study is calculated or saved (D5).
export const cableRunChoiceSchema = z
  .object({
    id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int(),
    design_check_status: z.string(),
    jurisdiction_profile: z.string(),
  })
  .strict();

const cableRunListSchema = z
  .object({ items: z.array(calculationRunSummarySchema) })
  .strict();

export type CableRunChoice = z.infer<typeof cableRunChoiceSchema>;

/** The cable runs stored under one project revision, newest first, as the selector lists them. */
export async function listCableRunsForRevision(
  projectRevisionId: string,
  signal?: AbortSignal,
): Promise<CableRunChoice[]> {
  const query = new URLSearchParams({ project_revision_id: projectRevisionId });

  const response = await fetch(`${CABLE_RUNS_URL}?${query.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Cable run list"), response.status);
  }

  const parsed = cableRunListSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS cable runs API.");
  }

  return parsed.data.items.map((item) =>
    cableRunChoiceSchema.parse({
      id: item.id,
      calculation_key: item.calculation_key,
      revision_number: item.revision_number,
      design_check_status: item.design_check_status,
      jurisdiction_profile: item.jurisdiction_profile,
    }),
  );
}
